import React from 'react';
import styled from 'styled-components';
import {
  C, Panel, PanelHeader, PanelTitle, PanelSubtitle, PanelBody, CountPill,
} from '../chrome';
import { formatAge, reasonLabel } from '../utils';

const Section = styled.div`
  & + & { margin-top: 2.4rem; }
`;

const SectionLabel = styled.div`
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${C.textMuted};
  margin-bottom: 1rem;
`;

const UtilRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 0.8rem;
`;

const UtilValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${C.textPrimary};
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
  span { color: ${C.textMuted}; font-weight: 500; font-size: 1.4rem; }
`;

const UtilPct = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${(p) => p.color || C.textMuted};
  font-variant-numeric: tabular-nums;
`;

const UtilTrack = styled.div`
  height: 1rem;
  background: ${C.surfaceAlt};
  border-radius: 9999px;
  overflow: hidden;
`;

const UtilFill = styled.div`
  height: 100%;
  width: ${(p) => p.pct}%;
  background: ${(p) => p.color};
  transition: width 0.4s ease;
`;

const PendingList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const PendingItem = styled.li`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid ${C.border};
  &:last-child { border-bottom: none; }
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.color || C.yellow};
`;

const ItemMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const ItemTitle = styled.div`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${C.textPrimary};
`;

const ItemSub = styled.div`
  font-size: 1.1rem;
  color: ${C.textMuted};
`;

const Age = styled.div`
  font-size: 1.1rem;
  color: ${C.textMuted};
  white-space: nowrap;
`;

const Empty = styled.div`
  padding: 1.6rem 0;
  text-align: center;
  color: ${C.textMuted};
  font-size: 1.2rem;
`;

const utilColor = (pct) => {
  if (pct >= 0.85) return C.red;
  if (pct >= 0.6) return C.yellow;
  return C.green;
};

const ageDotColor = (seconds) => {
  if (seconds >= 60 * 30) return C.red;     // > 30 min stuck
  if (seconds >= 60 * 10) return C.yellow;  // > 10 min stuck
  return C.green;
};

const RightNowPanel = ({ utilization = {}, oldestPending = [] }) => {
  const pct = Number(utilization.utilizationPct || 0);
  const onCall = Number(utilization.onCall || 0);
  const aligned = Number(utilization.alignedAndActive || 0);
  const color = utilColor(pct);

  return (
    <Panel>
      <PanelHeader>
        <div>
          <PanelTitle>Right Now</PanelTitle>
          <PanelSubtitle>Live agent and queue state</PanelSubtitle>
        </div>
        <CountPill color={color}>{(pct * 100).toFixed(0)}% busy</CountPill>
      </PanelHeader>

      <PanelBody>
        <Section>
          <SectionLabel>Pool utilization</SectionLabel>
          <UtilRow>
            <UtilValue>
              {onCall} <span>/ {aligned}</span>
            </UtilValue>
            <UtilPct color={color}>{(pct * 100).toFixed(0)}%</UtilPct>
          </UtilRow>
          <UtilTrack>
            <UtilFill pct={pct * 100} color={color} />
          </UtilTrack>
        </Section>

        <Section>
          <SectionLabel>Oldest pending cases</SectionLabel>
          {oldestPending.length === 0 ? (
            <Empty>Queue is empty — all caught up.</Empty>
          ) : (
            <PendingList>
              {oldestPending.map((p) => (
                <PendingItem key={p.id}>
                  <Dot color={ageDotColor(p.ageSeconds)} />
                  <ItemMain>
                    <ItemTitle>{p.application_no || `User #${p.max_user_id}`}</ItemTitle>
                    <ItemSub>
                      {p.attempts || 0} attempt{(p.attempts || 0) === 1 ? '' : 's'}
                      {' · '}
                      {reasonLabel(p.reason)}
                    </ItemSub>
                  </ItemMain>
                  <Age>{formatAge(p.ageSeconds)}</Age>
                </PendingItem>
              ))}
            </PendingList>
          )}
        </Section>
      </PanelBody>
    </Panel>
  );
};

export default RightNowPanel;
